import { useCallback, useEffect, useState } from 'preact/hooks';
import { isDismissed, recordDismissal } from '../pwa/installDismissal.js';
import { isIOS, isStandaloneDisplay } from '../pwa/platform.js';

/**
 * 138 US2 (research.md R2) — the install offer's state.
 *
 * `vite-plugin-pwa` has no install API for a non-Nuxt app: its `virtual:pwa-register` module
 * exposes update and offline callbacks only. So this drives the browser's own
 * `beforeinstallprompt` event directly, which is Chromium-only. iOS Safari never fires it and
 * offers no programmatic install at all, which is why `ios-hint` exists as a separate state
 * rather than a fallback message inside `installable`.
 */
export type InstallOfferState = 'hidden' | 'installable' | 'ios-hint';

export interface UseInstallPrompt {
  state: InstallOfferState;
  /** No-op unless `state` is `installable`. The captured event is single-use. */
  promptInstall: () => Promise<void>;
  /** Records the dismissal (30 days) and hides the offer. */
  dismiss: () => void;
}

/**
 * Not in TypeScript's DOM lib — Chromium ships it, the standard does not. Declared rather than
 * cast to `any` (Constitution VI).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function useInstallPrompt(): UseInstallPrompt {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [suppressed, setSuppressed] = useState(false);

  // Sampled once at mount. Both are stable for the lifetime of a page: the platform cannot change,
  // and a dismissal recorded during this render pass is already reflected by `suppressed`.
  const [eligible] = useState(() => !isStandaloneDisplay() && !isDismissed());
  const [ios] = useState(() => isIOS());

  useEffect(() => {
    function handleBeforeInstallPrompt(event: Event) {
      // Suppresses Chromium's own mini-infobar so the offer appears once, in our own words.
      event.preventDefault();
      setDeferredEvent(event as BeforeInstallPromptEvent);
    }

    function handleAppInstalled() {
      // Fires for an install started from the browser's menu too, so the offer clears without a
      // reload either way (FR-007).
      setSuppressed(true);
      setDeferredEvent(null);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return;

    // Cleared first: the event is spent by `prompt()` and cannot be shown twice, so a second tap
    // while the browser dialog is open must not reach it.
    setDeferredEvent(null);

    try {
      await deferredEvent.prompt();
      const { outcome } = await deferredEvent.userChoice;
      if (outcome === 'accepted') setSuppressed(true);
      // A decline needs nothing further: with the event spent there is nothing left to offer this
      // visit, and no dismissal record is written — it was the browser's dialog that was declined,
      // not our offer (data-model.md).
    } catch {
      // Chromium rejects `prompt()` when it is called without user activation, or a second time on
      // a spent event. Neither is worth surfacing to a customer — the offer is already cleared for
      // this visit — but it must not escape as an unhandled rejection either, since the caller
      // fires this from an onClick and nothing downstream is watching.
    }
  }, [deferredEvent]);

  const dismiss = useCallback(() => {
    recordDismissal();
    setSuppressed(true);
    setDeferredEvent(null);
  }, []);

  let state: InstallOfferState = 'hidden';
  if (eligible && !suppressed) {
    if (deferredEvent) state = 'installable';
    else if (ios) state = 'ios-hint';
  }

  return { state, promptInstall, dismiss };
}
